import React, { useEffect, useState } from 'react';
import { UpdateReport } from 'src/api';
import { DatePicker } from 'src/components/molecules';
import { HeaderSectionTitle } from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { calculateCaseDates } from 'src/utils/case';
import {
  DateFormats,
  formatDate,
  isSameOrAfter,
  isSameOrBefore,
  isValidDate,
} from 'src/utils/time';
import { DetailsContainer, Subtitle } from './DraftCaseDates.styles';

interface Props {
  currentReport: UpdateReport | null;
  updateReport: (report: UpdateReport) => void;
}

type CaseDate = 'submittedAt' | 'readAt' | 'investigatingAt' | 'closedAt';

export const DraftCaseDates = ({ currentReport, ...props }: Props) => {
  const todayDate = formatDate(
    new Date().toISOString(),
    DateFormats.YYYY_MM_DD,
  );

  const [caseDates, setCaseDates] = useState<
    ReturnType<typeof calculateCaseDates>
  >({
    submittedAt: { value: '', min: undefined, max: todayDate },
    companyCreatedAt: { value: '', min: undefined, max: todayDate },
    readAt: { value: '', min: undefined, max: todayDate },
    investigatingAt: { value: '', min: undefined, max: todayDate },
    closedAt: { value: '', min: undefined, max: todayDate },
  });

  const [errors, setErrors] = useState({
    submittedAt: false,
    readAt: false,
    investigatingAt: false,
    closedAt: false,
  });

  useEffect(() => {
    const dates = calculateCaseDates({
      submittedAt: currentReport?.submittedAt ?? null,
      readAt: currentReport?.caseAttributes?.readAt ?? null,
      investigatingAt: currentReport?.caseAttributes?.investigatingAt ?? null,
      closedAt: currentReport?.caseAttributes?.closedAt ?? null,
    });
    setCaseDates(dates);
  }, [currentReport]);

  const checkValidDates = (
    newDate: string,
    maxDate: string,
    minDate?: string,
  ) => {
    const noDate = newDate === '';
    if (noDate) return true;
    if (minDate) {
      return (
        isValidDate(newDate) &&
        isSameOrAfter(newDate, minDate) &&
        isSameOrBefore(newDate, maxDate)
      );
    }
    return isValidDate(newDate) && isSameOrBefore(newDate, maxDate);
  };

  const handleOnChange = (
    date: string,
    isValid: boolean,
    dateType: CaseDate,
  ) => {
    // Don't allow clearing of submittedAt
    if (dateType === 'submittedAt' && date === '') {
      return;
    }

    setErrors((prev) => ({ ...prev, [dateType]: !isValid }));
    if (isValid) {
      if (dateType === 'submittedAt') {
        props.updateReport({ ...currentReport, submittedAt: new Date(date) });
      } else {
        const clearDate = date === '' || date === null;
        props.updateReport({
          ...currentReport,
          caseAttributes: {
            ...currentReport?.caseAttributes,
            [dateType]: clearDate ? null : new Date(date),
          },
        });
      }
    }
  };

  return (
    <DetailsContainer>
      <HeaderSectionTitle>Case dates</HeaderSectionTitle>
      <div>
        <Subtitle>
          Submission <span>(Required)</span>
        </Subtitle>
        <DatePicker
          width="100%"
          max={caseDates?.submittedAt.max}
          onChange={(e) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates?.submittedAt.max,
            );
            handleOnChange(date, isValidDate, 'submittedAt');
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates?.submittedAt.max,
            );
            handleOnChange(date, isValidDate, 'submittedAt');
          }}
          value={caseDates.submittedAt.value ?? ''}
          error={errors.submittedAt}
          data-testid="submitted-date-picker"
        />
      </div>
      <div>
        <Subtitle>Read</Subtitle>
        <DatePicker
          width="100%"
          max={caseDates.readAt.max}
          min={caseDates.readAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.readAt.max,
              caseDates.readAt.min,
            );
            handleOnChange(date, isValidDate, 'readAt');
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.readAt.max,
              caseDates.readAt.min,
            );
            handleOnChange(date, isValidDate, 'readAt');
          }}
          value={caseDates.readAt.value ?? ''}
          error={errors.readAt}
          data-testid="read-date-picker"
        />
      </div>
      <div>
        <Subtitle>Investigating</Subtitle>
        <DatePicker
          width="100%"
          max={caseDates.investigatingAt.max}
          min={caseDates.investigatingAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.investigatingAt.max,
              caseDates.investigatingAt.min,
            );
            handleOnChange(date, isValidDate, 'investigatingAt');
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.investigatingAt.max,
              caseDates.investigatingAt.min,
            );
            handleOnChange(date, isValidDate, 'investigatingAt');
          }}
          value={caseDates.investigatingAt.value ?? ''}
          error={errors.investigatingAt}
          data-testid="investigating-date-picker"
        />
      </div>
      <div>
        <Subtitle>Closed</Subtitle>
        <DatePicker
          width="100%"
          max={caseDates.closedAt.max}
          min={caseDates.closedAt.min}
          onChange={(e) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.closedAt.max,
              caseDates.closedAt.min,
            );
            handleOnChange(date, isValidDate, 'closedAt');
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            const isValidDate = checkValidDates(
              date,
              caseDates.closedAt.max,
              caseDates.closedAt.min,
            );
            handleOnChange(date, isValidDate, 'closedAt');
          }}
          value={caseDates.closedAt.value ?? ''}
          error={errors.closedAt}
          data-testid="closed-date-picker"
        />
      </div>
    </DetailsContainer>
  );
};
